<template>
  <div
    class="cm-select"
    :class="{
      'cm-select--compact': compact,
      'cm-select--tags': showTags
    }"
    @focusin="handleFocusin"
    @focusout="handleFocusout"
  >
    <div v-if="showTags && multiple" class="cm-select__input-container">
      <div class="cm-select__input-wrapper">
        <cm-tag
          v-for="tag in selectedOptions"
          class="cm-select__tag"
          :key="tag"
          :value="tag.value"
          :label="tag.label"
          theme="panda"
          @delete="removeTag"
        />
        <input
          class="cm-select__input-container__search-input"
          type="text"
          v-model="searchText"
          @keydown.up='selectSibling(-1)'
          @keydown.down='selectSibling(1)'
          ref='searchInput'
          autocomplete="off"
          :placeholder="buttonLabel"
        />
      </div>
    </div>

    <button
      v-else
      type="button"
      class="cm-select__button"
      @focus="handleButtonFocus"
      @click="handleButtonFocus"
    >
      {{ buttonLabel }}
      <div class="cm-select__button-arrow">
        {{isOpen ? "navigateup" : "navigatedown"}}
      </div>
    </button>

    <div
      class="cm-select__popup"
      ref="popup"
      :class="{'cm-select__popup--is-open': isOpen}"
    >
      <div class="cm-select__search" v-if='includeSearch && !showTags'>
        <input
          class="cm-select__search-input"
          type="text"
          v-model="searchText"
          @keydown.up='selectSibling(-1)'
          @keydown.down='selectSibling(1)'
          ref='searchInput'
          autocomplete="off"
        />
      </div>

      <div class="cm-select__items">
        <label
          v-for="option in filteredOptions"
          :key="option.value"
          class="cm-select__item"
          :class="{
            'cm-select__item--is-selected': option.isSelected
          }"
          @mousedown.prevent
          @click="handleOptionClick"
        >
          <input
            class="cm-select__item-input"
            :type="multiple ? 'checkbox' : 'radio'"
            :name="name"
            :value="option.value"
            :checked="option.isSelected"
            @change="handleChange($event, option.value)"
            @click.stop
          />
          {{ option.label }}
          <span v-if="option.isSelected" class="cm-select__check ss-icon">check</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import CmTag from "./tag.vue"

export default {
  components: {
    CmTag,
  },

  props: {
    options: {
      type: Array
      // [{ name: String, value: [String, Number] }]
    },
    multiple: {
      type: Boolean
    },
    includeSearch: {
      type: Boolean
    },
    name: {
      type: String
    },
    nullLabel: {
      type: String
    },
    label: {
      type: String
    },
    value: {
      type: [String, Array]
    },
    compact: {
      type: Boolean,
    },
    showTags: {
      type: Boolean,
    }
  },

  data() {
    return {
      isOpen: false,
      searchText: ""
    };
  },

  computed: {
    allOptions() {
      const mergedArray = this.nullOption
        ? [this.nullOption, ...this.options]
        : this.options;

      return mergedArray.map(option => ({
        ...option,
        isSelected: this.multiple
          ? this.value.includes(option.value)
          : option.value === this.value
      }));
    },

    filteredOptions() {
      if (this.searchText) {
        return this.allOptions.filter(option =>
          option.label.toLowerCase().includes(this.searchText.toLowerCase())
        );
      } else {
        return this.allOptions;
      }

    },

    nullOption() {
      if (this.nullLabel) {
        return {
          label: this.nullLabel,
          value: null
        };
      }
    },

    currentOption() {
      if (!this.multiple) {
        return this.options.find(option => option.value === this.value);
      }
    },

    selectedOptions() {
      if (this.multiple) {
        return this.options.filter(option => this.value.includes(option.value));
      }
    },

    buttonLabel() {
      return this.currentOption
        ? this.currentOption.label
        : this.nullLabel || this.label;
    }
  },

  methods: {
    handleButtonFocus() {
      if (!this.isOpen) {
        const targets = [
          this.$el.querySelector(".cm-select__search-input"),
          this.$el.querySelector(".cm-select__item--is-selected"),
          this.$el.querySelector(".cm-select__item")
        ].filter(Boolean);

        targets[0].focus();
      }
    },

    handleFocusin(e) {
      this.isOpen = true;
    },

    handleFocusout(e) {
      if (!this.$el.contains(e.relatedTarget)) {
        this.isOpen = false;
      }
    },

    handleChange(e, value) {
      if (value === null) {
        this.resetValue();
      } else {
        let newValue;

        if (this.multiple) {
          if (e.target.checked) {
            newValue = [...this.value, value];
          } else {
            newValue = this.value.filter(el => el !== value);
          }
        } else {
          newValue = value;
        }

        this.$emit("input", newValue);
      }

      this.$refs.popup.scrollTop = 0; // chrome tries to scroll unscrollable divs to surface the newly-focused element...
    },

    removeTag(value) {
      const newValue = this.value.filter(el => el !== value);
      this.$emit("input", newValue);
    },

    resetValue() {
      this.$emit("input", this.multiple ? [] : null);
    },

    handleOptionClick(e) {
      // send close after focus event. closes when we want—
      // but also allows user to use ↑↓ keys to re-open the select
      if (this.isOpen && !this.multiple) {
        window.setTimeout(() => {
          this.isOpen = false;
        }, 10);
      } else {
        this.isOpen = true;
      }
    },

    selectSibling(direction) {
      const focusedElement = document.activeElement;
      const itemList = [...this.$el.querySelectorAll(".cm-select__item")];
      const selectedItem = this.$el.querySelector(
        ".cm-select__item--is-selected"
      );
      const currentIndex = itemList.indexOf(selectedItem);
      const newIndex =
        (currentIndex + direction + itemList.length) % itemList.length;

      if (!multiple) {
        itemList[newIndex].querySelector("input").click();
      }
      if (focusedElement === this.$refs.searchInput) {
        focusedElement.focus();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.cm-select {
  position: relative;
  display: inline-flex;
  height: 3rem;

  &--tags {
    display: block;
    height: auto;
  }

  &--compact {
    display: block;
    height: 2rem;
  }

  &__input-container {
    background: var(--bg-2);
    padding: 0 1rem;
    border-radius: var(--radius);
    border: 0 none;
    overflow-x: auto;
    overflow-y: hidden;
    height: 3rem;

    &__search-input {
      height: 3rem;
      background: none;
      border: none;
      flex: 1 0 auto;
      &:focus {
        outline: none;
      }
      &::-webkit-input-placeholder {
        color: var(--bg-2);
      }
      &::-moz-placeholder {
        color: var(--bg-2);
      }
      &::placeholder {
        color: var(--bg-2);
      }
    }
  }
  &__tag {
    margin-left: -.25rem;
    margin-right: .5rem;
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
  }

  &__button {
    @extend %b1;
    position: relative;
    width: 100%;
    height: 100%;
    padding: 0 2rem 0 1rem;
    color: var(--fg-2);
    background: var(--bg-2);
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    appearance: none;
    border: none;
    flex: 1 1 auto;
    text-align: left;
    border-radius: var(--radius);

    &-arrow {
      font-family: $ff_icon;
      width: 1rem;
      height: 1rem;
      border-radius: 50%;
      font-size: 0.6rem;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      padding-top: 3px;
      position: absolute;
      right: 0.5rem;
      top: 50%;
      transform: translate(0, -50%);
    }
  }

  &__popup {
    position: absolute;
    top: 100%;
    pointer-events: none;
    opacity: 0;
    background: var(--bg-elevated);
    box-shadow: $box_shadow_20;
    border-radius: var(--radius);
    min-width: 100%;
    max-height: 50vh;
    display: flex;
    flex-direction: column;
    z-index: 10;
    overflow: hidden;

    &--is-open {
      opacity: 1;
      pointer-events: unset;
    }
  }

  &__search {
    position: relative;
    padding: 0.5rem;
    &::after {
      content: $ss_search;
      position: absolute;
      right: 0.5rem;
      top: 50%;
      padding: 0.5rem;
      transform: translate(0, -50%);
      font-family: $ff_icon;
    }
    &-input {
      width: 100%;
      background: none;
      border: 1px solid var(--bg-2);
      padding: 0.25rem 1.5rem 0.25rem 0.5rem;
      appearance: none;
    }
  }

  &__items {
    overflow-x: hidden;
    overflow-y: auto;
  }

  &__item {
    display: block;
    line-height: 2rem;
    padding: 0 0.5rem;
    white-space: nowrap;
    &:hover,
    &:focus-within {
      background: var(--bg-2);
    }
    &--is-focused {
      background: var(--bg-2);
    }
    &--is-selected {
      display: flex;
      justify-content: space-between;
    }
    &-input {
      position: absolute;
      opacity: 0;
    }
  }
  &__check {
    font-size: 0.7rem;
  }
  &__item + &__item {
    border-top: 1px solid var(--bg-3);
  }
}
</style>



// WEBPACK FOOTER //
// app/javascript/components/select.vue?725d557c